/**
 * Bundle tier selector.
 *
 * Renders the tier + course choice as a product option in the buy box and adds
 * every chosen course to the cart as its own line item, at its own variant id,
 * via /cart/add.js with an `items` array. The discount is applied downstream by
 * the `bundle-tier-discount` Shopify Function; the maths here mirrors it exactly
 * so the buy box and the cart agree.
 *
 * Tier percentages arrive from the shop metafield `custom.bundle_tiers` through
 * the Liquid block. Nothing about pricing is defined in this file.
 */

const CENTS = 100;

class BundleSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.gdsReady === 'true') return;

    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
      this.courses = JSON.parse(this.dataset.courses || '[]');
    } catch (error) {
      // Malformed config: leave Horizon's native buy buttons in charge rather
      // than rendering a selector that cannot price itself.
      console.error('[bundle-selector] could not parse config', error);
      return;
    }

    if (!this.tiers.length || !this.courses.length) return;

    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });

    this.tileEls = Array.from(this.querySelectorAll('.gds-bundle__course'));
    this.tierEls = Array.from(this.querySelectorAll('.gds-bundle__tier'));

    this.buyButtons = this.#findBuyButtons();

    this.lockedIndex = this.courses.findIndex((c) => c.locked);
    if (this.lockedIndex < 0) this.lockedIndex = 0;

    this.selected = [this.lockedIndex];
    this.tierN = this.#initialTier();

    this.#bind();
    this.#bindNativeCta();
    this.#applyTier(this.tierN, { silent: true });

    this.dataset.gdsReady = 'true';
  }


  /* ── setup ───────────────────────────────────────────────────────────── */


  #initialTier() {
    const fromUrl = new URLSearchParams(window.location.search).get('bundle');
    const wanted = parseInt(fromUrl || this.dataset.defaultTier || '1', 10);
    return this.tiers.some((t) => t.n === wanted) ? wanted : this.tiers[0].n;
  }

  /**
   * Horizon's buy-buttons block lives in a sibling block, so reach for the
   * nearest one inside the same product section rather than the whole document
   * (a page can carry more than one product form).
   */
  /**
   * Horizon's add-to-cart button. The selector drives this rather than
   * supplying its own, so the theme's sticky add-to-cart bar keeps working: it
   * observes .buy-buttons-block for visibility and puppet-clicks this button.
   */
  #nativeCta() {
    return this.buyButtons?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /** The element holding the button's label, keeping its icon intact. */
  #nativeCtaSlot() {
    const cta = this.#nativeCta();
    return cta?.querySelector('.add-to-cart-text__content') ?? cta;
  }

  #bindNativeCta() {
    const cta = this.#nativeCta();
    if (!cta) return;

    // Remember the theme's own wording so a single-course selection gets it back.
    this._nativeCtaLabel = this.#nativeCtaSlot()?.textContent.trim() ?? '';

    cta.addEventListener('click', (event) => {
      // One course is just this product: leave Horizon's own flow alone.
      if (this.tierN <= 1) return;

      // The form is bound with on:submit="/handleSubmit", so preventing the
      // click stops the implicit submit and with it the native add. The
      // button's own handleClick only drives the animation, so it can run.
      event.preventDefault();
      this.#addToCart();
    });
  }

  #findBuyButtons() {
    const scope = this.closest('.shopify-section, .section') || document;
    return scope.querySelector('.buy-buttons-block');
  }

  #bind() {
    this.tierEls.forEach((el) => {
      el.addEventListener('click', () => this.#applyTier(parseInt(el.dataset.n, 10)));
      el.addEventListener('keydown', (event) => this.#onTierKeydown(event));
    });

    this.tileEls.forEach((el) => {
      el.addEventListener('click', () => this.#toggleCourse(parseInt(el.dataset.idx, 10)));
    });

  }

  /**
   * The courses the gift flow should send.
   *
   * Public on purpose: blocks/gift-this-course.liquid has no knowledge of this
   * selector and reads the product form's single hidden input, so without this
   * it would gift only the course whose page this is. Returns the locked
   * course's live variant id so a variant change is respected.
   */
  giftSelection() {
    const lockedVariantId = this.#lockedVariantId();
    return this.selected.map((i) => ({
      id: i === this.lockedIndex ? lockedVariantId : this.courses[i].variantId,
      title: this.courses[i].title,
    }));
  }

  /**
   * Keep the gift block honest about what is about to be gifted: trigger label,
   * modal title, subtitle, and the product pill that names a single course.
   *
   * The modal is appended to document.body, so only the trigger actually lives
   * inside .gds-gift-root — everything else has to be looked up document-wide.
   * Bundle wording comes from the gift block's own settings via data attributes,
   * so it stays editable in the theme editor and neither block needs to know
   * about the other's schema.
   */
  #syncGiftCopy() {
    const root = document.querySelector('.gds-gift-root');
    if (!root) return;

    const names = this.selected.map((i) => this.courses[i].title);
    const bundle = names.length > 1;

    if (!this._giftCopy) this._giftCopy = {};

    // Cache each element's configured text on first sight, so single-course
    // selections restore exactly what the merchant set rather than a guess.
    const swap = (el, key, bundleText) => {
      if (!el) return;
      if (this._giftCopy[key] === undefined) this._giftCopy[key] = el.textContent;
      el.textContent = bundle && bundleText ? bundleText : this._giftCopy[key];
    };

    swap(
      document.querySelector('.gds-gift-subtitle'),
      'subtitle',
      `Gifting all ${names.length} courses in this bundle: ${names.join(', ')}.`
    );
    swap(root.querySelector('.gds-gift-trigger-label'), 'label', root.dataset.bundleLabel);
    swap(document.querySelector('.gds-gift-title'), 'heading', root.dataset.bundleHeading);

    // The pill names one product, which is simply wrong for a bundle.
    const pill = document.querySelector('.gds-gift-product-pill');
    if (pill) pill.hidden = bundle;
  }

  /* ── keyboard: radiogroup semantics ──────────────────────────────────── */

  #onTierKeydown(event) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    const current = this.tierEls.findIndex((el) => el.getAttribute('aria-checked') === 'true');
    const last = this.tierEls.length - 1;
    let next;

    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current >= last ? 0 : current + 1;
    else next = current <= 0 ? last : current - 1;

    this.#applyTier(parseInt(this.tierEls[next].dataset.n, 10));
    this.tierEls[next].focus();
  }

  /* ── state transitions ───────────────────────────────────────────────── */

  #applyTier(n, { silent = false } = {}) {
    const tier = this.tiers.find((t) => t.n === n);
    if (!tier) return;

    this.tierN = n;

    // Trim from the end, never dropping the locked course.
    if (this.selected.length > n) {
      this.selected = this.selected.filter((i) => i === this.lockedIndex).concat(
        this.selected.filter((i) => i !== this.lockedIndex)
      );
      this.selected = this.selected.slice(0, n);
    }

    this.#autofill();
    this.#render();

    if (!silent) {
      this.#track('gds_bundle_tier_select', {
        bundle_tier: n,
        bundle_tier_name: tier.name,
      });
    }
  }

  /**
   * Fill any empty slots so Add to cart is never gated behind a second
   * decision. Preference order comes from the block's auto-fill setting, then
   * the rest of the collection in its own order.
   */
  #autofill() {
    if (this.tierN >= this.courses.length) {
      this.selected = this.courses.map((_, i) => i).filter((i) => this.courses[i].available !== false);
      return;
    }

    const preferred = (this.dataset.autofill || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean)
      .map((h) => this.courses.findIndex((c) => c.handle === h))
      .filter((i) => i > -1);

    const order = preferred.concat(this.courses.map((_, i) => i));

    for (const i of order) {
      if (this.selected.length >= this.tierN) break;
      if (this.selected.includes(i)) continue;
      if (this.courses[i].available === false) continue;
      this.selected.push(i);
    }
  }

  #toggleCourse(idx) {
    const course = this.courses[idx];
    if (!course || course.locked) return;
    if (this.tierN >= this.courses.length) return; // whole catalogue tier
    if (course.available === false) return;

    const at = this.selected.indexOf(idx);

    if (at > -1) {
      this.selected.splice(at, 1);
    } else if (this.selected.length < this.tierN) {
      this.selected.push(idx);
    } else {
      return; // at capacity — the tile is dimmed, so this is a no-op by design
    }

    this.#render();
    this.#track('gds_bundle_course_select', {
      bundle_tier: this.tierN,
      course_handle: course.handle,
      course_title: course.title,
      selected: at === -1,
    });
  }

  /* ── pricing (mirrors the discount function) ─────────────────────────── */

  #tier() {
    return this.tiers.find((t) => t.n === this.tierN) || this.tiers[0];
  }

  #totals() {
    const tier = this.#tier();
    const prices = this.selected.map((i) => this.courses[i]?.price || 0);

    // Price any empty slot at the current course's rate. A part-filled bundle
    // would otherwise quote a discounted figure the customer cannot actually
    // buy — the tier's real cost is the honest number to show while the CTA
    // reads "Pick 1 more course".
    const padPrice = this.courses[this.lockedIndex]?.price || 0;
    while (prices.length < this.tierN) prices.push(padPrice);

    const full = prices.reduce((sum, p) => sum + p, 0);
    const discount = Math.round((full * tier.percent) / 100);
    return { full, discount, total: full - discount, tier };
  }

  #money(cents) {
    const currency = this.dataset.currency || 'AUD';
    const locale = this.dataset.locale || 'en';
    const amount = cents / CENTS;

    // narrowSymbol renders AUD as "$" rather than "A$", matching Liquid's money
    // filter used for the server-rendered figures on this same page. Falls back
    // to the default symbol if a browser does not support it.
    for (const currencyDisplay of ['narrowSymbol', 'symbol']) {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay }).format(amount);
      } catch {
        /* try the next display mode */
      }
    }
    return `${amount.toFixed(2)} ${currency}`;
  }

  /* ── render ──────────────────────────────────────────────────────────── */

  #render() {
    const { full, discount, total, tier } = this.#totals();
    const remaining = Math.max(0, this.tierN - this.selected.length);
    const complete = remaining === 0;
    const atCapacity = this.selected.length >= this.tierN;
    const wholeCatalogue = this.tierN >= this.courses.length;

    this.tierEls.forEach((el) => {
      const on = parseInt(el.dataset.n, 10) === this.tierN;
      el.setAttribute('aria-checked', String(on));
      el.tabIndex = on ? 0 : -1;
    });

    this.tileEls.forEach((el, i) => {
      const on = this.selected.includes(i);
      const locked = this.courses[i]?.locked;
      el.setAttribute('aria-pressed', String(on));
      el.dataset.dim = String(!on && atCapacity);
      if (locked || wholeCatalogue) {
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('aria-disabled');
      }
    });

    this.#set('tierName', tier.name);
    this.#set('counter', `${this.selected.length} / ${this.tierN}`);
    this.#set(
      'bundleNames',
      this.selected.map((i) => this.courses[i].title).join(', ')
    );

    this.#set('now', this.#money(total));
    if (this.refs.was) {
      this.refs.was.textContent = this.#money(full);
      this.refs.was.hidden = discount <= 0;
    }
    this.#set(
      'per',
      this.tierN > 1 ? `${this.#money(Math.round(total / this.tierN))} per course · lifetime access` : 'Lifetime access'
    );

    this.#renderCallout({ remaining, complete, discount, total });
    this.#renderCta({ remaining, complete, total });
    this.#syncStickyBar({ full, discount, total });
    this.#syncGiftCopy();
  }

  /**
   * Horizon's sticky bar is built from the single product on the page, so with a
   * bundle selected it would advertise one course at one price. Retitle it.
   *
   * Its image and its button are deliberately left alone: the button puppet-
   * clicks the main CTA, which the selector already drives, and its label span
   * contains Horizon's quantityDisplay ref, which must not be rebuilt.
   */
  #syncStickyBar({ full, discount, total }) {
    const bar = document.querySelector('sticky-add-to-cart');
    if (!bar) return;

    const title = bar.querySelector('.sticky-add-to-cart__title');
    const price = bar.querySelector('.sticky-add-to-cart__price');

    // Capture what the theme rendered, so one course restores it exactly.
    if (!this._stickyDefaults) {
      this._stickyDefaults = {
        title: title?.textContent ?? '',
        priceHtml: price?.innerHTML ?? '',
      };
    }
    const original = this._stickyDefaults;

    if (this.tierN <= 1) {
      if (title) title.textContent = original.title;
      if (price) price.innerHTML = original.priceHtml;
      return;
    }

    // Kept deliberately short. At 375px this column shares the bar with an
    // image, two prices and the button; the course list was truncated to
    // "Dollhous..." and the title to "3-course ...", so the saving earns the
    // space instead.
    if (title) title.textContent = `${this.tierN} courses`;

    if (price) {
      price.textContent = this.#money(total);
      if (discount > 0) {
        const was = document.createElement('s');
        was.className = 'gds-sticky-was';
        was.textContent = this.#money(full);
        price.appendChild(was);
      }
    }
  }

  #renderCallout({ remaining, complete, discount, total }) {
    const tokens = {
      '[count]': String(remaining),
      '[courses]': remaining === 1 ? 'course' : 'courses',
      '[each]': this.#money(Math.round(total / this.tierN)),
      '[save]': this.#money(discount),
      '[total]': this.#money(total),
    };

    let template;
    if (!complete) {
      template = this.dataset.copyIncomplete;
    } else if (this.tierN === 1) {
      const next = this.tiers.find((t) => t.n > 1);
      if (next) {
        const nextFull = (this.courses[this.lockedIndex]?.price || 0) * next.n;
        const nextTotal = nextFull - Math.round((nextFull * next.percent) / 100);
        tokens['[each]'] = this.#money(Math.round(nextTotal / next.n));
      }
      template = this.dataset.copySingle;
    } else {
      template = this.dataset.copyComplete;
    }

    this.#set('callout', this.#fill(template, tokens), { html: true });
  }

  #renderCta({ remaining, complete, total }) {
    const cta = this.#nativeCta();
    const slot = this.#nativeCtaSlot();
    if (!cta || !slot) return;

    // At one course the theme's own wording and behaviour apply unchanged.
    if (this.tierN <= 1) {
      if (this._nativeCtaLabel) slot.textContent = this._nativeCtaLabel;
      cta.disabled = false;
      return;
    }

    const tokens = {
      '[count]': String(remaining),
      '[courses]': remaining === 1 ? 'course' : 'courses',
      '[total]': this.#money(total),
    };

    slot.textContent = this.#fill(
      complete ? this.dataset.ctaComplete : this.dataset.ctaIncomplete,
      tokens
    ).replace(/<[^>]*>/g, '');
    cta.disabled = !complete;
  }

  #fill(template, tokens) {
    return Object.entries(tokens).reduce(
      (out, [token, value]) => out.split(token).join(`<strong>${value}</strong>`),
      template || ''
    );
  }

  #set(ref, value, { html = false } = {}) {
    const el = this.refs[ref];
    if (!el) return;
    if (html) el.innerHTML = value;
    else el.textContent = value;
  }


  /* ── cart ────────────────────────────────────────────────────────────── */

  /**
   * Prefer the live value of the product form's variant input so a variant
   * change on the current product is respected without a separate listener.
   */
  #lockedVariantId() {
    const scope = this.closest('.shopify-section, .section') || document;
    const input = scope.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
    const live = input && parseInt(input.value, 10);
    return live || this.courses[this.lockedIndex]?.variantId;
  }

  async #addToCart() {
    const cta = this.#nativeCta();
    const slot = this.#nativeCtaSlot();
    if (!cta || cta.disabled || this._busy) return;

    this._busy = true;
    const label = slot.textContent;
    cta.disabled = true;
    if (this.refs.error) this.refs.error.hidden = true;

    const lockedVariantId = this.#lockedVariantId();
    const items = this.selected.map((i) => ({
      id: i === this.lockedIndex ? lockedVariantId : this.courses[i].variantId,
      quantity: 1,
    }));

    // Ask Shopify to re-render the cart sections in the same round trip, the
    // way product-form.js and the gift block both do.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart.');

      const { total } = this.#totals();
      this.#track('gds_bundle_add_to_cart', {
        bundle_tier: this.tierN,
        bundle_tier_name: this.#tier().name,
        value: total / CENTS,
        currency: this.dataset.currency,
        items: this.selected.length,
        courses: this.selected.map((i) => this.courses[i].handle),
        course_titles: this.selected.map((i) => this.courses[i].title),
      });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'bundle-selector',
            data: {
              source: 'product-form-component',
              itemCount: items.length,
              sections: data.sections || {},
            },
          },
        })
      );

      slot.textContent = label;
    } catch (error) {
      if (this.refs.error) {
        this.refs.error.textContent = error.message || 'Something went wrong. Please try again.';
        this.refs.error.hidden = false;
      }
      slot.textContent = label;
    } finally {
      cta.disabled = false;
      this._busy = false;
    }
  }

  /* ── analytics ───────────────────────────────────────────────────────── */

  #track(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      product_id: this.dataset.productId,
      product_title: this.dataset.productTitle,
      ...payload,
    });
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}
